import React, { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useKeycloakToken } from 'shared/auth/useKeycloak';
import NotFoundPage from 'shared/components/layout/NotFoundPage';
import UnsavedSectionPrompt from 'shared/components/configPanel/UnsavedSectionPrompt';
import RouteDetails from './RouteDetails';
import RouteDetailsSource from './RouteDetailsSources';
import RouteDetailsLog from './RouteDetailsLog';
import RouteProgressIndicator from './NewRouteProgressIndicator/RouteProgressIndicator';
import { isValidRouteSubpage, routeTabPath } from './routeSubpages';
import { useRouteLogsTab } from './routeDetails/useRouteLogsTab';
import { useRouteSourcesTab } from './routeDetails/useRouteSourcesTab';
import RouteConfigurationsTab from './routeDetails/RouteConfigurationsTab';
import RouteDetailsSkeleton from './routeDetails/RouteDetailsSkeleton';
import { PendingSetup, getQuickStart } from './routeDetails/quickStart';
import { useRouteHelp } from './routeDetails/useRouteHelp';
import { useRouteLeaveGuard } from './routeDetails/useRouteLeaveGuard';
import { useRouteRecord } from './routeDetails/useRouteRecord';
import { useRouteSubpageRedirects } from './routeDetails/useRouteSubpageRedirects';

// The Route page: the Route itself, its tab strip and Quick Start, and the
// open tab. Each tab keeps its own state; the page keeps only what the tabs
// share.
export function ManageRouteDetails() {
  const { route_id, subpage } = useParams<'route_id' | 'subpage'>();
  const navigate = useNavigate();
  const location = useLocation();
  const [token] = useKeycloakToken();
  // The :subpage route param accepts any string — an unknown value renders no
  // tab. Validate the param itself, not the resolved tab: an absent subpage is
  // valid and resolves below, once the Route's health is known.
  const isValidSubpage = isValidRouteSubpage(subpage);
  // A Route has exactly three addresses: /routes/:id, /routes/:id/sources and
  // /routes/:id/logs. The bare one IS the Configurations tab. An unhealthy
  // Route opens on Logs instead, but that happens once, as a redirect on
  // arrival (see useRouteSubpageRedirects) — deriving it here on every render
  // made Configurations unreachable, because the tab strip sends
  // Configurations back to the bare URL and this would immediately re-answer
  // "logs".
  const effectiveSubpage = subpage || 'configurations';

  const record = useRouteRecord({
    routeId: route_id,
    token,
    enabled: isValidSubpage,
  });
  const { route, routeProvider, routeName, destinations, pageLoaded } = record;

  // Reported by the Configurations tab, which is the only one that knows.
  const [pendingSetup, setPendingSetup] = useState<PendingSetup>(null);
  const [rulesPanelOpen, setRulesPanelOpen] = useState(false);
  const { pendingEditsRef, guardedLeave, leavePrompt, closeLeavePrompt } =
    useRouteLeaveGuard();

  useRouteHelp({
    providerType: routeProvider.type,
    subpage: effectiveSubpage,
    rulesPanelOpen,
  });
  useRouteSubpageRedirects({
    routeId: route_id,
    subpage,
    effectiveSubpage,
    providerId: routeProvider?.id,
    pageLoaded,
    routeStatus: route.status,
  });

  // The Logs tab pins its filter under the nav by scrolling the page down. That
  // scroll offset is the window's, not the tab's, and a tab switch is a route
  // change — which never resets it — so every other tab used to open already
  // scrolled past its own header. Reset on each tab change. Keyed on the subpage
  // and not on the location: the Configurations panel navigates within
  // "configurations" (account/flow selection) and must keep the user's place.
  React.useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [effectiveSubpage]);

  // The Logs and Sources tabs keep their filters when you switch away and
  // back, so the page holds their state.
  const logsTab = useRouteLogsTab({
    routeId: route_id ?? '',
    providerId: route.provider?.id,
    destinationIds: destinations.map((d) => d.id),
    active: effectiveSubpage === 'logs',
    loadedRoute: record.routeQuery.data
      ? {
          status: record.routeQuery.data.route.status,
          loadedAt: record.routeQuery.dataUpdatedAt,
        }
      : null,
  });
  const sourcesTab = useRouteSourcesTab({
    routeId: route_id ?? '',
    providerId: routeProvider.id,
    active: effectiveSubpage === 'sources',
    pageLoaded,
  });

  // Configurations is what the bare URL shows, so it does not get a segment
  // of its own — otherwise the same tab would have two addresses.
  const handleSelectSubPage = (tab: string) =>
    guardedLeave(() => navigate(routeTabPath(route_id, tab)));

  const quickStart = getQuickStart({
    routeProvider,
    hasDestinations: destinations.length > 0,
    pendingSetup,
  });

  if (record.notFound || !isValidSubpage) return <NotFoundPage />;

  if (!pageLoaded) return <RouteDetailsSkeleton subpage={effectiveSubpage} />;

  return (
    <div>
      <div className="-mx-4 -mb-6 flex min-h-[calc(100vh-80px)] flex-col bg-teal-t10 px-4 pb-6 md:-mx-8 md:min-h-[calc(100vh-70px)] md:px-8 desktop:-mx-[60px] desktop:px-[60px]">
        {/* Direct children of this column, deliberately: RouteDetails
            returns the title block plus a STICKY tab strip, and a
            sticky element can only travel inside its own parent. */}
        <RouteDetails
          route={route}
          providerEnabled={routeProvider.enabled !== false}
          routeName={routeName}
          subPage={effectiveSubpage}
          onSelectSubPage={handleSelectSubPage}
        />
        {quickStart.hasWork && (
          /* The progress bar is a wide horizontal stepper that can't fit on
             small screens, so hide it below 780px and show it above.
             NOTE for reviewers: hiding the Quick Start on small screens is an
             accepted, intentional tradeoff — not an oversight to flag. */
          <div className="mx-auto mt-8 hidden w-full max-w-list-table px-0 md:px-4 min-[780px]:block">
            <RouteProgressIndicator
              stepZeroStatus="completed"
              stepOneStatus={quickStart.providerStatus}
              stepTwoStatus={quickStart.destinationsStatus}
              webhookStepStatus={
                quickStart.isWebhookRoute ? quickStart.webhookStatus : undefined
              }
              pendingSetupCount={pendingSetup?.total ?? 0}
            />
          </div>
        )}
        <div className="mx-auto w-full max-w-list-table">
          {effectiveSubpage === 'configurations' && (
            <RouteConfigurationsTab
              record={record}
              pendingEditsRef={pendingEditsRef}
              onNeedsSetupChange={setPendingSetup}
              onRulesPanelOpenChange={setRulesPanelOpen}
              highlightAddDestination={
                !!(location.state as any)?.highlightAddDestination
              }
            />
          )}
          {effectiveSubpage === 'sources' && (
            <RouteDetailsSource {...sourcesTab} pageLoaded={pageLoaded} />
          )}
          {effectiveSubpage === 'logs' && (
            <RouteDetailsLog
              {...logsTab}
              pageLoaded={pageLoaded}
              // Unhealthy Route (the flow map's red state): logs open
              // pre-filtered to Warning minimum so the signal is on top.
              defaultMinimumWarning={route.status === 'unhealthy'}
            />
          )}
        </div>
      </div>
      <UnsavedSectionPrompt prompt={leavePrompt} onClose={closeLeavePrompt} />
    </div>
  );
}
